// The play and service metrics (spec/17 §3 M-06 … M-11, M-14, M-15; F-ANL-01).
//
// Each takes plain facts the server reads for a window — sessions with their charged seconds, the stations and the
// venue's open hours, the plays the stations reported, the calls for help — and returns the metric, or `null` when
// the window has no facts to measure (spec/17 §1.5: empty is not zero). Times are epoch milliseconds, durations
// integer seconds, money bigint minor units. No clock is read: the window is always a parameter.
import { roundHalfUpBigInt } from "../money/round.js";
import { ratioOf } from "./ledger-metrics.js";
/**
 * **M-06 / M-07 / M-08** (spec/17 §3). `sessionPlayers` are the player ids of the window's sessions (repeats
 * allowed); `firstSessionAt` maps a player to the start of their first-ever session at the scope (epoch ms);
 * `priorActive` is the set of players active in the prior comparable window. A player whose first session is
 * unknown is not counted new — the conservative reading, so "new" can never be true for nearly everyone (spec/17 §8).
 * `null` when nobody played.
 */
export function playerCountsOf(sessionPlayers, firstSessionAt, priorActive, window) {
    const active = new Set(sessionPlayers);
    if (active.size === 0)
        return null;
    let fresh = 0;
    let returning = 0;
    for (const player of active) {
        const first = firstSessionAt.get(player);
        if (first !== undefined && first >= window.from && first < window.to)
            fresh += 1;
        if (priorActive.has(player))
            returning += 1;
    }
    return { active: active.size, new: fresh, returning };
}
/** The prior comparable window: the same length, immediately before (spec/17 M-08). */
export function priorWindowOf(window) {
    return { from: window.from - (window.to - window.from), to: window.from };
}
/**
 * **M-09 Sessions** (spec/17 §3): the count of sessions, play seconds as the sum of their segments' charged seconds,
 * and the average length. A session listed twice (a reader that joined its segments) counts once, with its seconds
 * summed. `null` with no sessions.
 */
export function sessionTotalsOf(facts) {
    const bySession = new Map();
    for (const fact of facts)
        bySession.set(fact.sessionId, (bySession.get(fact.sessionId) ?? 0) + fact.chargedSeconds);
    if (bySession.size === 0)
        return null;
    const playSeconds = [...bySession.values()].reduce((sum, seconds) => sum + seconds, 0);
    return {
        sessions: bySession.size,
        playSeconds,
        averageSeconds: Number(roundHalfUpBigInt(BigInt(playSeconds), BigInt(bySession.size))),
    };
}
function overlapMillis(a, b) {
    return Math.max(0, Math.min(a.to, b.to) - Math.max(a.from, b.from));
}
/**
 * **M-10 Occupancy / utilisation** (spec/17 §3): station-seconds in session ÷ station-seconds available in open hours,
 * overall and by class. Session time outside the open hours is not counted (the numerator never exceeds what could
 * have been sold), and a station's overlapping intervals count once. `open` is the venue's open stretches around the
 * window; a venue with no hours set passes the whole window as one stretch. `null` when nothing was available —
 * no stations, or closed for the whole window.
 */
export function occupancyOf(stations, intervals, open, window) {
    const openInWindow = open
        .map((stretch) => ({ from: Math.max(stretch.opensAt, window.from), to: Math.min(stretch.closesAt, window.to) }))
        .filter((stretch) => stretch.to > stretch.from);
    const openMillis = openInWindow.reduce((sum, stretch) => sum + (stretch.to - stretch.from), 0);
    if (stations.length === 0 || openMillis === 0)
        return null;
    const classOf = new Map(stations.map((station) => [station.stationId, station.classId]));
    // Per station: its intervals, merged, so a double-read never counts a second twice.
    const byStation = new Map();
    for (const interval of intervals) {
        if (!classOf.has(interval.stationId) || interval.to <= interval.from)
            continue;
        const list = byStation.get(interval.stationId) ?? [];
        list.push({ from: interval.from, to: interval.to });
        byStation.set(interval.stationId, list);
    }
    const inSessionByClass = new Map();
    for (const [stationId, list] of byStation) {
        list.sort((a, b) => a.from - b.from);
        const merged = [];
        for (const interval of list) {
            const last = merged[merged.length - 1];
            if (last !== undefined && interval.from <= last.to)
                last.to = Math.max(last.to, interval.to);
            else
                merged.push({ ...interval });
        }
        let millis = 0;
        for (const interval of merged)
            for (const stretch of openInWindow)
                millis += overlapMillis(interval, stretch);
        const classId = classOf.get(stationId) ?? '';
        inSessionByClass.set(classId, (inSessionByClass.get(classId) ?? 0) + millis);
    }
    const stationsByClass = new Map();
    for (const station of stations)
        stationsByClass.set(station.classId, (stationsByClass.get(station.classId) ?? 0) + 1);
    const openSeconds = Math.floor(openMillis / 1000);
    const figure = (inSessionMillis, stationCount) => {
        const inSessionSeconds = Math.floor(inSessionMillis / 1000);
        const availableSeconds = openSeconds * stationCount;
        const ratio = ratioOf(BigInt(inSessionSeconds), BigInt(availableSeconds)) ?? { numerator: 0n, denominator: 0n, basisPoints: 0 };
        return { inSessionSeconds, availableSeconds, ratio };
    };
    const byClass = {};
    let totalInSession = 0;
    for (const [classId, count] of [...stationsByClass.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
        const millis = inSessionByClass.get(classId) ?? 0;
        totalInSession += millis;
        byClass[classId] = figure(millis, count);
    }
    return { total: figure(totalInSession, stations.length), byClass };
}
/** Occupancy **now** (the floor's "in session of total", F-ANL-02): stations in session ÷ stations offered. */
export function occupancyNowOf(stationsInSession, stationsOffered) {
    return ratioOf(BigInt(stationsInSession), BigInt(stationsOffered));
}
// ---------------------------------------------------------------------------------------------
// M-11 Title plays / play time
// ---------------------------------------------------------------------------------------------
/** The longest a single play counts for: a day (spec/17 M-11). */
export const TITLE_PLAY_CAP_SECONDS = 24 * 60 * 60;
/**
 * **M-11 Title plays / play time** (spec/17 §3): from `game_play`, deduplicated by play id, each capped at 24 h, with
 * distinct players. `null` with no plays.
 */
export function titlePlaysOf(facts) {
    const unique = new Map();
    for (const fact of facts)
        if (!unique.has(fact.playId))
            unique.set(fact.playId, fact);
    if (unique.size === 0)
        return null;
    const titles = new Map();
    const players = new Set();
    let seconds = 0;
    for (const fact of unique.values()) {
        const capped = Math.min(Math.max(0, fact.seconds), TITLE_PLAY_CAP_SECONDS);
        seconds += capped;
        players.add(fact.playerId);
        const title = titles.get(fact.titleId) ?? { name: fact.name, plays: 0, seconds: 0, players: new Set() };
        title.plays += 1;
        title.seconds += capped;
        title.players.add(fact.playerId);
        titles.set(fact.titleId, title);
    }
    return {
        plays: unique.size,
        seconds,
        players: players.size,
        titles: [...titles.entries()]
            .map(([titleId, title]) => ({ titleId, name: title.name, plays: title.plays, seconds: title.seconds, players: title.players.size }))
            .sort((a, b) => b.seconds - a.seconds || (a.titleId < b.titleId ? -1 : a.titleId > b.titleId ? 1 : 0)),
    };
}
/** `seconds` of play at `hourlyRateMinor` per hour, rounded half up to the minor unit. */
export function valueOfSeconds(seconds, hourlyRateMinor) {
    return roundHalfUpBigInt(BigInt(seconds) * hourlyRateMinor, 3600n);
}
/**
 * **M-14 Comps and discounts** (spec/17 §3): comp minutes valued at their rate, and the discount amount. `null` when
 * nothing was comped and no discount was given (`discounts` null — no discounts account moved — and no comp).
 */
export function compsAndDiscountsOf(comps, discounts) {
    if (comps.length === 0 && discounts === null)
        return null;
    let compSeconds = 0;
    let compValue = 0n;
    let unvaluedCompSeconds = 0;
    for (const comp of comps) {
        compSeconds += comp.seconds;
        if (comp.hourlyRateMinor === null)
            unvaluedCompSeconds += comp.seconds;
        else
            compValue += valueOfSeconds(comp.seconds, comp.hourlyRateMinor);
    }
    return { compSeconds, compValue, unvaluedCompSeconds, discounts: discounts ?? 0n };
}
/** The median of whole seconds, the lower-middle's and upper-middle's mean rounded half up; `null` when empty. */
export function medianSeconds(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1)
        return sorted[middle] ?? null;
    const low = sorted[middle - 1] ?? 0;
    const high = sorted[middle] ?? 0;
    return Number(roundHalfUpBigInt(BigInt(low + high), 2n));
}
function callFigures(calls) {
    const seconds = (from, to) => Math.max(0, Math.round((to - from) / 1000));
    return {
        count: calls.length,
        medianAcknowledgeSeconds: medianSeconds(calls.flatMap((call) => (call.acknowledgedAt === null ? [] : [seconds(call.raisedAt, call.acknowledgedAt)]))),
        medianResolveSeconds: medianSeconds(calls.flatMap((call) => (call.closedAt === null ? [] : [seconds(call.raisedAt, call.closedAt)]))),
    };
}
function groupBy(items, key) {
    const groups = new Map();
    for (const item of items) {
        const name = key(item);
        if (name === null)
            continue;
        const list = groups.get(name) ?? [];
        list.push(item);
        groups.set(name, list);
    }
    return groups;
}
/**
 * **M-15 Calls for help** (spec/17 §3): the count, the median time to acknowledge and to resolve, by category and by
 * the staff member who acknowledged. `null` with no calls.
 */
export function callsForHelpOf(calls) {
    if (calls.length === 0)
        return null;
    const sortedRecord = (groups) => Object.fromEntries([...groups.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([name, list]) => [name, callFigures(list)]));
    return {
        ...callFigures(calls),
        byCategory: sortedRecord(groupBy(calls, (call) => call.category)),
        byStaff: sortedRecord(groupBy(calls, (call) => call.staffId)),
    };
}
//# sourceMappingURL=activity-metrics.js.map